/**
 * Taking the door down — the one ceremony in `/you`.
 *
 * Pausing used to be a switch with its consequence written beside it. That was
 * right while the consequence was small; it is not small. A live door is an
 * address strangers are being sent to, and turning it off is the difference
 * between a doorman answering and nobody answering — with no error page to warn
 * anyone, because the address still resolves. A control that does that on one
 * unconsidered click is a control that will do it by accident.
 *
 * So: a quiet entry, then four lines and a typed confirmation. The four lines
 * are the whole truth in the order somebody needs it — what strangers meet,
 * what stops, what is KEPT, and how to undo it. The third line is the one this
 * ceremony exists for: everything about "taking something down" in every other
 * product means deletion, and here it means none.
 *
 * The confirmation is the handle IN CAPITALS. Not because capitals are magic —
 * because the shape of the act should match its weight, and typing your own
 * address out is the smallest gesture that cannot be made by a stray click. The
 * server asks for exactly the same string and refuses without it, so this is a
 * ceremony with a rule behind it rather than a screen being solemn on its own.
 *
 * PUTTING IT BACK UP HAS NO CEREMONY. One button, no typing, no confirmation:
 * reopening is not a thing anybody regrets, and making it symmetrical would be
 * ritual for its own sake.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { LinkButton, QuietButton } from "../ui/button";

export const TAKE_DOWN_LABEL = "Take the door down";
export const TAKEDOWN_TITLE = "Take the door down?";
export const CONFIRM_LABEL = "Take it down";
export const TAKEDOWN_CANCEL = "Not now";

/**
 * The four lines, in order, and never reordered: two about what stops, one
 * about what does not, one about getting back.
 */
export const TAKEDOWN_LINES: readonly string[] = [
  "Knockers are turned away at the door — the address still resolves.",
  "Nob stops answering for you there.",
  "Nothing is deleted. Your threads and rules stay as they are.",
  "You can put it back up whenever you like.",
];

/** The bold half of line three, which is the line the whole ceremony is for. */
export const NOTHING_DELETED = "Nothing is deleted.";

export function confirmPrompt(handle: string): string {
  return `Type ${handle.toUpperCase()} to confirm`;
}

/** The server refused the confirmation; the door is exactly as it was. */
export const CONFIRM_REFUSED = "That is not the handle. The door is still up.";
export const TAKEDOWN_UNREACHABLE = "Nob is not answering. The door is still up.";

/** What a confirmed takedown came back as. */
export type TakedownOutcome = "ok" | "refused" | "unreachable";

/**
 * The one control in the app that keeps a RED focus ring instead of the shared
 * `focus` role. Everywhere else a ring only says "you are here"; on the button
 * that takes a live door down it is also the last thing a reader sees before
 * pressing, and it should not look like every other button.
 *
 * The label was `text-red-200`, which was legible on a dark ground and all but
 * invisible on a light one — 1.3:1 on white. `danger` is the themed value and
 * reads on all three.
 */
const DESTRUCTIVE =
  "inline-flex h-11 items-center justify-center rounded-lg border border-danger/50 px-5 text-sm " +
  "font-medium text-danger transition-colors duration-75 hover:bg-danger/10 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg " +
  "disabled:pointer-events-none disabled:opacity-50";

export function TakedownControl({
  handle,
  busy,
  onConfirm,
}: {
  handle: string;
  busy: boolean;
  /** Sends `{ status: "paused", confirm_handle }` and says how it went. */
  onConfirm: (confirmHandle: string) => Promise<TakedownOutcome>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      {/* Quiet: an action of this size does not get to look like the primary
          thing on the screen, and it is not one somebody arrives looking for. */}
      <LinkButton type="button" onClick={() => setOpen(true)}>
        {TAKE_DOWN_LABEL}
      </LinkButton>
      {open && (
        <TakedownModal
          handle={handle}
          busy={busy}
          onConfirm={onConfirm}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function TakedownModal({
  handle,
  busy,
  onConfirm,
  onClose,
}: {
  handle: string;
  busy: boolean;
  onConfirm: (confirmHandle: string) => Promise<TakedownOutcome>;
  onClose: () => void;
}) {
  const titleId = useId();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  const required = handle.toUpperCase();
  /**
   * Case is the confirmation. A lowercase handle is what somebody types without
   * reading; the capitals are what somebody types having read.
   */
  const matches = typed.trim() === required;

  useEffect(() => {
    field.current?.focus();
  }, []);

  // Escape is a way out of every dialog anybody has ever used; nothing has
  // happened yet, so leaving costs nothing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const confirm = useCallback(() => {
    if (!matches || busy) return;
    setError(null);
    void onConfirm(required).then((outcome) => {
      if (outcome === "ok") return onClose();
      // The door did not move. The modal stays where it is and says so — a
      // dismissed dialog after a failed takedown would read as success.
      setError(outcome === "refused" ? CONFIRM_REFUSED : TAKEDOWN_UNREACHABLE);
    });
  }, [busy, matches, onClose, onConfirm, required]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* The room behind goes quiet rather than away: this is a decision about
          the door on screen, not a new place. */}
      <div className="absolute inset-0 bg-bg/80 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="nob-enter relative w-full max-w-md rounded-2xl border border-border bg-bg p-6 shadow-2xl"
      >
        <h2 id={titleId} className="text-lg font-semibold tracking-tight text-text">
          {TAKEDOWN_TITLE}
        </h2>

        <ul className="mt-4 space-y-2.5">
          {TAKEDOWN_LINES.map((line) => (
            <li key={line} className="text-[14px] leading-relaxed text-text-soft">
              {line.startsWith(NOTHING_DELETED) ? (
                <>
                  <strong className="font-semibold text-text">{NOTHING_DELETED}</strong>
                  {line.slice(NOTHING_DELETED.length)}
                </>
              ) : (
                line
              )}
            </li>
          ))}
        </ul>

        <form
          className="mt-6"
          onSubmit={(e) => {
            e.preventDefault();
            confirm();
          }}
        >
          <label
            htmlFor="takedown-confirm"
            className="block text-[12px] uppercase tracking-[0.12em] text-faint"
          >
            {confirmPrompt(handle)}
          </label>
          <input
            id="takedown-confirm"
            ref={field}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            disabled={busy}
            onChange={(e) => setTyped(e.target.value)}
            className="mt-1.5 h-9 w-full rounded-lg border border-border bg-surface/60 px-3 font-mono
                       text-sm tracking-wide text-text focus-visible:border-border-loud
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40
                       disabled:opacity-50"
          />

          {error && (
            <p role="alert" className="mt-3 text-[13px] text-danger">
              {error}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button type="submit" disabled={!matches || busy} className={DESTRUCTIVE}>
              {CONFIRM_LABEL}
            </button>
            <QuietButton type="button" onClick={onClose} disabled={busy}>
              {TAKEDOWN_CANCEL}
            </QuietButton>
          </div>
        </form>
      </div>
    </div>
  );
}
